/**
 * Players page roster — fetches the Owners Hub Stats API's TWO player data sets and
 * hands them to {@link mergePlayers}.
 *
 * 1. **`select`/`players`** (`POST /select/data-set`) is the BASE list: every
 *    registered account on the brand. It is not a window query (no `start_date`), so
 *    it is cached per brand.
 * 2. **`aggregations`/`daily_player_activity`** is the ENRICHMENT: only the players
 *    who wagered or deposited, carrying the money figures.
 *
 * Neither data set does server-side search, and only the registration list can sort
 * on strings. Since the page must sort/filter across BOTH (e.g. "registered this
 * month, ordered by amount wagered"), both lists are paged into memory (once per brand
 * per session, or on every open with `refreshOnMount`) and every filter / sort / page
 * runs client-side over the merged array (see `roster-view.ts`).
 *
 * The two fetches are independent, and so are their failure and cap states: each is
 * reported on its own so the page can say WHICH half is missing. A half-loaded roster
 * is still shown (with a note); only a roster with no rows at all is an error.
 *
 * **Shared hook — `error` alone is not enough.** Pulse and Casino settings read this
 * roster too, and a half-loaded one has `error: false` while being an UNDER-COUNT:
 * without the registration half it lists only the players who transacted (5 of 217 on
 * the sampled brand), and without the activity half every money figure is a
 * substituted zero. Any consumer that COUNTS the roster or divides by it must check
 * `registrationsFailed`, and any consumer reading the money figures must check
 * `activityFailed`, or it will publish a number the data does not support.
 */
import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchAllPlayerActivity,
  fetchAllPlayers,
  selectPlayerList,
  selectPlayerListError,
  selectPlayerListLoading,
  selectPlayerRoster,
  selectPlayerRosterError,
  selectPlayerRosterLoading,
  type PlayerRosterRef,
} from '@/store/slices/ownerHubStats';

import type { Player } from '../../mock-data/types';
import { mergePlayers, type RawRow } from './roster-merge';

// "All-time": a window wide enough to cover any brand's whole history. FIXED (not
// `Date.now()`-relative) so the cache key is stable across the session; the end is
// EXCLUSIVE, so a far-future date keeps today's rows in range. Applies to the
// ACTIVITY data set only — the registration list takes no window.
const ROSTER_WINDOW = { startDate: '2020-01-01', endDate: '2100-01-01' };

export type PlayersRosterResult = {
  data: Player[];
  loading: boolean;
  /** Nothing loadable: the roster is empty AND something failed. */
  error: boolean;
  /**
   * The activity fetch failed. Every money/count figure on screen is a zero this
   * page substituted, NOT a measured zero, so the page has to say so: silently
   * rendering "$0 deposited" for a paying brand is the worst of the failure modes.
   */
  activityFailed: boolean;
  /** The registration fetch failed: only players with activity could be listed. */
  registrationsFailed: boolean;
  /** The REGISTRATION list hit its fetch cap: older registrations aren't loaded. */
  truncatedRegistrations: boolean;
  /**
   * The ACTIVITY list hit its cap. Players past it are real transactors that the
   * merge could only zero, so they read as "no activity yet" until the cap lifts.
   */
  truncatedActivity: boolean;
  /** Registrations actually loaded — the subject of the truncation note. */
  registrationCount: number;
  /** Activity rows actually loaded — the subject of the activity truncation note. */
  activityCount: number;
};

export type PlayersRosterOptions = {
  /**
   * Page both lists again on every mount and brand switch, even when they are cached, keeping the cached rows on screen
   * meanwhile. The Players page sets it: Pulse, Logos and the FTUX logo step fill the same session cache first, so
   * without it a player who registered since then stayed invisible until a reload.
   */
  refreshOnMount?: boolean;
};

/**
 * @param casinoInstanceId Active brand id (`displayBrand?.id`); `undefined` keeps the
 *        roster in its loading state until a brand resolves.
 */
export const usePlayersRoster = (
  casinoInstanceId: string | undefined,
  { refreshOnMount = false }: PlayersRosterOptions = {},
): PlayersRosterResult => {
  const dispatch = useAppDispatch();

  const activityRef: PlayerRosterRef | undefined = casinoInstanceId
    ? { casinoInstanceId, startDate: ROSTER_WINDOW.startDate, endDate: ROSTER_WINDOW.endDate }
    : undefined;

  const listEntry = useAppSelector((s) => selectPlayerList(s, casinoInstanceId));
  const listLoadingFlag = useAppSelector((s) => selectPlayerListLoading(s, casinoInstanceId));
  const listErrorMsg = useAppSelector((s) => selectPlayerListError(s, casinoInstanceId));

  const activityEntry = useAppSelector((s) => selectPlayerRoster(s, activityRef));
  const activityLoadingFlag = useAppSelector((s) => selectPlayerRosterLoading(s, activityRef));
  const activityErrorMsg = useAppSelector((s) => selectPlayerRosterError(s, activityRef));

  // By default, fetch once per (brand, session) per list: paging each is several
  // requests, so a cached list is reused (and wiped on logout / brand teardown via
  // `resetOwnerHubStats`). A failed load (no cached entry) retries on the next visit or
  // brand switch. The two run independently so one failing degrades rather than
  // blanking the page. With `refreshOnMount`, each mount and each brand switch pages the
  // list again over the cached one; only the brand last refreshed is remembered, so the
  // entry the refresh lands doesn't start another (a switch back to a brand re-pages it).
  const listRefreshedFor = React.useRef<string | undefined>(undefined);
  const activityRefreshedFor = React.useRef<string | undefined>(undefined);

  React.useEffect(() => {
    if (!casinoInstanceId) return;
    const refresh = refreshOnMount && listRefreshedFor.current !== casinoInstanceId;
    if (listEntry && !refresh) return;
    listRefreshedFor.current = casinoInstanceId;
    dispatch(fetchAllPlayers({ casinoInstanceId }));
  }, [dispatch, casinoInstanceId, listEntry, refreshOnMount]);

  React.useEffect(() => {
    if (!casinoInstanceId) return;
    const refresh = refreshOnMount && activityRefreshedFor.current !== casinoInstanceId;
    if (activityEntry && !refresh) return;
    activityRefreshedFor.current = casinoInstanceId;
    dispatch(
      fetchAllPlayerActivity({
        casinoInstanceId,
        startDate: ROSTER_WINDOW.startDate,
        endDate: ROSTER_WINDOW.endDate,
      }),
    );
  }, [dispatch, casinoInstanceId, activityEntry, refreshOnMount]);

  const data = React.useMemo<Player[]>(() => {
    const registrations: RawRow[] = Array.isArray(listEntry?.data) ? (listEntry?.data as RawRow[]) : [];
    const activity: RawRow[] = Array.isArray(activityEntry?.data) ? (activityEntry?.data as RawRow[]) : [];
    return mergePlayers(registrations, activity);
  }, [listEntry, activityEntry]);

  // Stale-while-revalidate, per list: skeleton only while there is NO cached data
  // yet (a missing brand reads as loading; a first load that failed stops loading and
  // falls through to the error/empty state; a background refresh keeps rows on screen).
  const listPending = listEntry === undefined && (listLoadingFlag || listErrorMsg === undefined);
  const activityPending = activityEntry === undefined && (activityLoadingFlag || activityErrorMsg === undefined);
  const loading = !casinoInstanceId || listPending || activityPending;

  const registrationsFailed = !loading && listEntry === undefined && listErrorMsg !== undefined;
  const activityFailed = !loading && activityEntry === undefined && activityErrorMsg !== undefined;
  // Either half alone still makes a usable page, so a failure only becomes the hard
  // error state when it leaves NOTHING to render: registrations down but activity up
  // lists the players who transacted (which is what this page showed before the
  // registration list was wired), and activity down lists everyone with the figures
  // flagged as unmeasured rather than reported as zero.
  //
  // Only the REGISTRATION half can turn an empty roster into an error, because only
  // it can answer "this brand has no players". A new brand whose registration list
  // came back empty has been authoritatively answered — telling that owner "we
  // couldn't load your players" because the activity call happened to fail would
  // report a failure where there was none.
  const error = registrationsFailed && data.length === 0;

  return {
    data,
    loading,
    error,
    activityFailed,
    registrationsFailed,
    // The two caps are separate facts with separate consequences, so they are never
    // OR-ed into one flag: an activity cap says nothing about missing registrations.
    truncatedRegistrations: listEntry?.truncated ?? false,
    truncatedActivity: activityEntry?.truncated ?? false,
    registrationCount: Array.isArray(listEntry?.data) ? listEntry.data.length : 0,
    activityCount: Array.isArray(activityEntry?.data) ? activityEntry.data.length : 0,
  };
};

export default usePlayersRoster;
